import { appendAudioTranscriptToNotes, transcribeAudioFile } from './audio-file-transcription';
import { classifyAiServiceFailure, createConsecutiveFailureCounter } from './ai-service-failure';
import {
    folderAudioDescriptor, folderAudioReceipt, withFolderAudioReceiptEntry,
    type FolderAudioReceiptEntry,
} from './folder-audio-receipt';
import { createSelectedFileScope, fileMatchesSelectedScope } from './selected-file-scope';
import { deleteVideoBlob, getProjectFolderAudioBlobs } from './video-blob-store';
import {
    assertEvidencePreparationCurrent, checkpointPreparedEvidence,
    type ProjectEvidencePreparationOptions, type ProjectEvidencePreparationResult,
} from './project-evidence-preparation-types';

type Row = Record<string, any>;
interface AudioNotesDependencies {
    listBlobs: (projectId: string) => Promise<Row[]>;
    deleteBlob: (blobId: number, projectId: string) => Promise<boolean>;
    transcribe: typeof transcribeAudioFile;
}

// The same pacing as a video's transcript (project-video-evidence-preparation):
// a busy service is waited out once by the run, and only the second failure in
// a row sets the recording aside. A recording set aside for that reason keeps
// its bytes and is tried again by a run started after this long.
const TRANSIENT_SERVICE_FAILURES_BEFORE_SET_ASIDE = 2;
const SERVICE_SET_ASIDE_RETRY_AFTER_MS = 15 * 60 * 1000;
const transientServiceFailuresByProject = createConsecutiveFailureCounter(20 * 60 * 1000);

/** Test seam: forget the failure counts. */
export function _resetFolderAudioFailuresForTests(): void {
    transientServiceFailuresByProject.reset();
}

function isAbort(error: unknown): boolean {
    return (error as { name?: unknown } | null)?.name === 'AbortError';
}

/** The file a recording was imported as, for the selection and the notes header. */
function sourceOf(record: Row) {
    return {
        id: String(record.sourceFileId || ''),
        name: String(record.originalName || record.name || 'recording'),
        relativePath: String(record.relativePath || ''),
        size: Number(record.size ?? record.sizeBytes) || 0,
        lastModified: Number(record.lastModified) || 0,
    };
}

/**
 * A folder import's audio files reach Inspector Notes the way a Stage 1 drop
 * puts them there (Peter, 2026-10-23: the routes must behave the same): each
 * recording is transcribed, appended with Stage 1's header, once, and recorded
 * on the form. Its bytes wait in the project's video_blobs store until then
 * (preprocessed-row-builder) and are cleared once the outcome is saved. A
 * recording that cannot be used is set aside with its reason and the run goes
 * on, as a Stage 1 drop marks the file and imports the rest.
 */
export async function prepareProjectAudioNotes(
    options: ProjectEvidencePreparationOptions,
    overrides: Partial<AudioNotesDependencies> = {},
): Promise<ProjectEvidencePreparationResult> {
    const deps: AudioNotesDependencies = {
        listBlobs: getProjectFolderAudioBlobs, deleteBlob: deleteVideoBlob, transcribe: transcribeAudioFile,
        ...overrides,
    };
    const check = () => assertEvidencePreparationCurrent(options);
    check();
    let project = options.project;
    const scope = createSelectedFileScope(options.selectedFileIds);
    const records = (await deps.listBlobs(project.id))
        .filter(record => sourceOf(record).id && fileMatchesSelectedScope(sourceOf(record), scope));
    check();
    if (records.length === 0) return { project, changed: false };
    let changed = false;
    const saveOutcome = async (record: Row, entry: FolderAudioReceiptEntry, notes?: string) => {
        const source = sourceOf(record);
        const formData = withFolderAudioReceiptEntry({ ...(project.formData || {}), ...(notes !== undefined ? { notes } : {}) }, source.id, entry);
        project = await checkpointPreparedEvidence(options, { ...project, formData });
        changed = true;
    };
    const clear = async (record: Row) => {
        // Cleared only after the outcome is saved: a failed save leaves the
        // bytes for the next run. A failed clear is tidied by the next run.
        try { await deps.deleteBlob(record.id, project.id); } catch { /* the receipt already stops a second append */ }
    };
    for (const record of records) {
        check();
        const source = sourceOf(record);
        const descriptor = folderAudioDescriptor(source);
        const prior = folderAudioReceipt(project.formData).sources[source.id];
        const base = { descriptor, name: source.name, ...(source.relativePath ? { relativePath: source.relativePath } : {}) };
        if (prior?.descriptor === descriptor) {
            // Already added, or set aside for good: a rescan or a second
            // preparation in the same run never adds it twice.
            if (prior.status === 'appended' || prior.retry !== true) { await clear(record); continue; }
            const setAsideAt = Date.parse(String(prior.at || ''));
            if (Number.isFinite(setAsideAt) && Date.now() - setAsideAt < SERVICE_SET_ASIDE_RETRY_AFTER_MS) {
                // Say so: a silent skip reads as 'the recording was used'.
                options.onProgress?.(0, `Recording set aside a few minutes ago, not retried yet: ${source.name}`);
                continue;
            }
        }
        const setAside = (reason: string, retry = false) => saveOutcome(record, {
            status: 'set-aside', ...base, at: new Date().toISOString(), reason, ...(retry ? { retry: true } : {}),
        });
        if (typeof record.rejectedReason === 'string' && record.rejectedReason) {
            await setAside(record.rejectedReason);
            await clear(record);
            continue;
        }
        if (!options.apiKey) continue;
        options.onProgress?.(0, `Transcribing audio: ${source.name}`);
        let text: string;
        try {
            const file = new File([record.blob], source.name, {
                type: record.mimeType || record.blob?.type || 'audio/mpeg', lastModified: source.lastModified,
            });
            const transcript = await deps.transcribe(file, {
                apiKey: options.apiKey,
                ...(options.model ? { model: options.model } : {}),
                ...(options.audioModel ? { audioModel: options.audioModel } : {}),
                projectId: project.id,
                ...(options.signal ? { signal: options.signal } : {}),
                ...(options.onProgress ? { onProgress: options.onProgress } : {}),
            });
            text = String(transcript?.text || '').trim();
        } catch (error) {
            // A Stop or a superseded run is not a problem with the recording.
            if (isAbort(error) || options.signal?.aborted) throw error;
            check();
            const failure = classifyAiServiceFailure(error);
            if (failure.fromService) {
                // Credit, sign-in and configuration need the operator: no
                // recording will fare better, so the run stops as it does for
                // every other AI call.
                if (!failure.transient) throw error;
                const failures = transientServiceFailuresByProject.increment(String(project.id));
                if (failures < TRANSIENT_SERVICE_FAILURES_BEFORE_SET_ASIDE) throw error;
                await setAside(`The AI transcription service was unavailable on ${failures} attempts in a row (${failure.title || 'AI service error'}). Retry later to add its transcript.`, true);
                continue;
            }
            const detail = String((error as { message?: unknown } | null)?.message || error || 'unknown error').slice(0, 200);
            await setAside(`The recording could not be transcribed: ${detail}`);
            await clear(record);
            continue;
        }
        check();
        transientServiceFailuresByProject.clear(String(project.id));
        if (!text) {
            await setAside('No speech was heard in the recording, so nothing was added to the notes.');
            await clear(record);
            continue;
        }
        const notes = appendAudioTranscriptToNotes(project.formData?.notes, { name: source.name }, text);
        await saveOutcome(record, { status: 'appended', ...base, at: new Date().toISOString() }, notes);
        await clear(record);
    }
    check();
    return { project, changed };
}
